/**
 * VitalStrip — 5-cell vitals row for the player modal (and the full profile,
 * Unit 2b). Grade · %ile · Price · Mkt Val · MLB rank. Canonical paths only
 * (no flat fallbacks); themeable gold via var(--team-primary).
 */
import React from 'react';
import { dynastyTheme } from '../../../../../services/colorService';
import PropTypes from 'prop-types';
import { mobileColors } from '../../../../../services/mobileStyleService';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;

const fmtPct = (p) => (p != null ? `${Math.round(p)}` : '--');
// The tile price formatter — whole dollars, no "M" suffix ($10, $48). Exported
// so Financials and the player-header contract sub-line reuse it (no 2nd one).
export const fmtPrice = (v) => (v != null ? `$${Number(v).toFixed(0)}` : '--');
const fmtRank = (r) => (r != null ? `#${r}` : '--');

const Cell = ({ label, value, color, divider }) => (
  <div style={{
    flex: 1, minWidth: 0, position: 'relative', textAlign: 'center', padding: '6px 4px',
  }}>
    {/* INSET vertical divider between cells (absolute span, not a full-height border) */}
    {divider && <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 5, bottom: 5, width: 1, background: C.border }} />}
    <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: mobileColors.textMuted, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: color || mobileColors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
  </div>
);

const VitalStrip = ({ player }) => {
  const fantasy = player?.analytics?.fantasy;
  const financial = player?.league_context?.financial;
  const overall = player?.analytics?.rankings?.overall;

  const cells = [
    { label: 'Grade', value: fantasy?.letter_grade ?? '--', color: GOLD },
    { label: '%ile', value: fmtPct(fantasy?.percentile) },
    { label: 'Price', value: fmtPrice(financial?.baseline_price) },
    { label: 'Mkt Val', value: fmtPrice(financial?.current_market_value), color: mobileColors.success },
    // rankings.overall.mlb is an OBJECT ({rank, total_players, ...}) — mirror
    // RanksTabContent which reads overallRank.rank. The cell shows #<rank>.
    { label: 'MLB', value: fmtRank(overall?.mlb?.rank) },
  ];

  // Flat: hairline dividers between cells only, no per-cell boxes, no top border.
  // No BOTTOM border either — the TabNavigator's top hairline is the single
  // shared line between the strip and the tabs (they read as one line, no gap).
  return (
    <div style={{ display: 'flex', background: C.surface[740] }}>
      {cells.map((c, i) => <Cell key={c.label} label={c.label} value={c.value} color={c.color} divider={i > 0} />)}
    </div>
  );
};

VitalStrip.propTypes = { player: PropTypes.object };

export default VitalStrip;
